import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../service/auth';
import { EyeIconComponent } from '../../shared/svg/eye-icon';
import { EyeSlashIconComponent } from '../../shared/svg/eye-slash-icon';

@Component({
	selector: 'app-signup',
	imports: [CommonModule, FormsModule, EyeIconComponent, EyeSlashIconComponent],
	templateUrl: './signup.html',
	styleUrl: './signup.scss'
})
export class SignupComponent {
	private authService = inject(AuthService);
	private router = inject(Router);

	// Form data
	email = '';
	password = '';
	confirmPassword = '';

	// UI state
	isLoading = signal<boolean>(false);
	errorMessage = signal<string>('');
	showPassword = signal<boolean>(false);
	showConfirmPassword = signal<boolean>(false);

	// Signal to track if avatar is clicked (showing surprised state)
	private isSurprised = signal(false);

	// Computed property to determine which avatar image to display
	avatarImage = computed(() => {
		// Show surprised image when clicked
		if (this.isSurprised()) {
			return 'assets/images/avatar/Crustopher-Surprised.png';
		}
		return 'assets/images/avatar/Crustopher-Happy.png';
	});

	/**
	 * Handle form submission for account creation
	 */
	async onSubmit(): Promise<void> {
		// Reset error message
		this.errorMessage.set('');

		// Validate form
		if (!this.validateForm()) {
			return;
		}

		this.isLoading.set(true);

		try {
			await this.authService.signUp(this.email, this.password);
			// User will be automatically signed in after successful creation
			this.router.navigate(['/app/home']);
		} catch (error: any) {
			this.errorMessage.set(error);
		} finally {
			this.isLoading.set(false);
		}
	}

	/**
	 * Validate form inputs
	 */
	private validateForm(): boolean {
		if (!this.email.trim()) {
			this.errorMessage.set('Please enter your email address.');
			return false;
		}

		if (!this.isValidEmail(this.email)) {
			this.errorMessage.set('Please enter a valid email address.');
			return false;
		}

		if (!this.password) {
			this.errorMessage.set('Please enter a password.');
			return false;
		}

		if (this.password.length < 6) {
			this.errorMessage.set('Password must be at least 6 characters long.');
			return false;
		}

		if (this.password !== this.confirmPassword) {
			this.errorMessage.set('Passwords do not match.');
			return false;
		}

		return true;
	}

	/**
	 * Validate email format
	 */
	private isValidEmail(email: string): boolean {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	}

	/**
	 * Toggle password visibility
	 */
	togglePasswordVisibility(): void {
		this.showPassword.set(!this.showPassword());
	}

	/**
	 * Toggle confirm password visibility
	 */
	toggleConfirmPasswordVisibility(): void {
		this.showConfirmPassword.set(!this.showConfirmPassword());
	}

	/**
	 * Navigate to login page
	 */
	goToLogin(): void {
		this.router.navigate(['/auth/login']);
	}

	/**
	 * Navigate to forgot password page
	 */
	goToForgotPassword(): void {
		this.router.navigate(['/auth/forgot-password']);
	}

	// Handle avatar click to show surprised state
	onAvatarClick() {
		this.isSurprised.set(true);
		// Revert back to happy state after 500ms
		setTimeout(() => {
			this.isSurprised.set(false);
		}, 500);
	}
}
